/**
 * Custom Hook for VAT Calculations
 * Handles both VAT-inclusive and VAT-exclusive pricing
 */

import { useState, useEffect } from 'react';
import { settingsService } from '../services/settings.service';

interface VATSettings {
  rate: number; // VAT rate as percentage (e.g., 16 for 16%)
  inclusive: boolean; // Whether prices include VAT
}

interface VATCalculation {
  subtotal: number; // Amount before VAT
  vatAmount: number; // VAT amount
  total: number; // Total including VAT
}

export function useVAT() {
  const [vatSettings, setVatSettings] = useState<VATSettings>({
    rate: 16,
    inclusive: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVATSettings = async () => {
      try {
        const response = await settingsService.getTenantSettings();
        if (response.data) {
          setVatSettings({
            rate: response.data.taxRate || 16,
            inclusive: response.data.vatInclusive || false,
          });
        }
      } catch (error) {
        console.error('Failed to load VAT settings:', error);
        // Use defaults
      } finally {
        setLoading(false);
      }
    };

    loadVATSettings();
  }, []);

  /**
   * Calculate VAT for a given amount
   * @param amount - The base amount (item price or subtotal)
   * @param quantity - Optional quantity multiplier
   * @returns VATCalculation object with subtotal, VAT amount, and total
   */
  const calculateVAT = (amount: number, quantity: number = 1): VATCalculation => {
    const itemTotal = amount * quantity;

    if (vatSettings.inclusive) {
      // VAT Inclusive: Price already includes VAT
      // Example: If price is 116 and VAT is 16%
      // Total = 116 (this is what customer pays)
      // Subtotal = 116 / 1.16 = 100
      // VAT = 116 - 100 = 16
      const total = itemTotal;
      const subtotal = itemTotal / (1 + vatSettings.rate / 100);
      const vatAmount = total - subtotal;

      return {
        subtotal: Number(subtotal.toFixed(2)),
        vatAmount: Number(vatAmount.toFixed(2)),
        total: Number(total.toFixed(2)),
      };
    } else {
      // VAT Exclusive: VAT will be added on top
      // Example: If price is 100 and VAT is 16%
      // Subtotal = 100
      // VAT = 100 * 0.16 = 16
      // Total = 100 + 16 = 116
      const subtotal = itemTotal;
      const vatAmount = subtotal * (vatSettings.rate / 100);
      const total = subtotal + vatAmount;

      return {
        subtotal: Number(subtotal.toFixed(2)),
        vatAmount: Number(vatAmount.toFixed(2)),
        total: Number(total.toFixed(2)),
      };
    }
  };

  /**
   * Calculate VAT for multiple items
   * @param items - Array of items with price and quantity
   * @returns VATCalculation object with combined subtotal, VAT amount, and total
   */
  const calculateOrderVAT = (items: Array<{ price: number; quantity: number }>): VATCalculation => {
    let totalSubtotal = 0;
    let totalVAT = 0;
    let totalAmount = 0;

    items.forEach(item => {
      const itemCalc = calculateVAT(item.price, item.quantity);
      totalSubtotal += itemCalc.subtotal;
      totalVAT += itemCalc.vatAmount;
      totalAmount += itemCalc.total;
    });

    return {
      subtotal: Number(totalSubtotal.toFixed(2)),
      vatAmount: Number(totalVAT.toFixed(2)),
      total: Number(totalAmount.toFixed(2)),
    };
  };

  /**
   * Get VAT information as formatted strings
   */
  const getVATInfo = () => ({
    rate: `${vatSettings.rate}%`,
    mode: vatSettings.inclusive ? 'Inclusive' : 'Exclusive',
    description: vatSettings.inclusive
      ? `Prices include ${vatSettings.rate}% VAT`
      : `${vatSettings.rate}% VAT will be added at checkout`,
  });

  return {
    vatSettings,
    loading,
    calculateVAT,
    calculateOrderVAT,
    getVATInfo,
  };
}
